import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

/**
 * Component that displays text with character-by-character typing animation
 */
export const TypingText = ({ text, speed = 30, onComplete, onProgress }) => {
    const [displayedText, setDisplayedText] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (currentIndex < text.length) {
            const timeout = setTimeout(() => {
                setDisplayedText(prev => prev + text[currentIndex]);
                setCurrentIndex(prev => prev + 1);
                if (onProgress) {
                    onProgress();
                }
            }, speed);
            return () => clearTimeout(timeout);
        } else if (onComplete && currentIndex === text.length && text.length > 0) {
            onComplete();
        }
    }, [currentIndex, text, speed, onComplete, onProgress]);

    // Reset when text changes
    useEffect(() => {
        setDisplayedText('');
        setCurrentIndex(0);
    }, [text]);

    return <span>{displayedText}</span>;
};

/**
 * Component for displaying markdown content with section-level diff highlighting.
 * - Detects changed sections by comparing old vs new content
 * - Highlights only changed sections with blue fade animation
 * - Unchanged sections remain static (no flicker)
 */
export const TypingMarkdown = ({ content, previousContent = '', typingSpeed = 20, onAnimationStateChange }) => {
    const [displayContent, setDisplayContent] = useState(content || '');
    const [changedSections, setChangedSections] = useState(new Set());
    const previousContentRef = useRef(previousContent);
    const highlightTimeoutRef = useRef(null);

    useEffect(() => {
        if (!content) return;

        // If this is the first content or no previous content, show immediately
        if (!previousContentRef.current || previousContentRef.current === 'Waiting for input...') {
            setDisplayContent(content);
            previousContentRef.current = content;
            if (onAnimationStateChange) {
                onAnimationStateChange(false);
            }
            return;
        }

        // If content hasn't changed, do nothing
        if (content === previousContentRef.current) {
            if (onAnimationStateChange) {
                onAnimationStateChange(false);
            }
            return;
        }

        // Detect changed sections
        const oldSections = splitIntoSections(previousContentRef.current);
        const newSections = splitIntoSections(content);
        const changed = new Set();

        newSections.forEach((section, index) => {
            if (!oldSections[index] || oldSections[index] !== section) {
                changed.add(index);
            }
        });

        // Update content and mark changed sections
        setDisplayContent(content);
        setChangedSections(changed);

        if (onAnimationStateChange) {
            onAnimationStateChange(true);
        }

        // Clear highlights after animation completes
        if (highlightTimeoutRef.current) {
            clearTimeout(highlightTimeoutRef.current);
        }
        highlightTimeoutRef.current = setTimeout(() => {
            setChangedSections(new Set());
            if (onAnimationStateChange) {
                onAnimationStateChange(false);
            }
        }, 3300); // Match animation duration

        previousContentRef.current = content;

        return () => {
            if (highlightTimeoutRef.current) {
                clearTimeout(highlightTimeoutRef.current);
            }
        };
    }, [content, onAnimationStateChange]);

    return (
        <div className="typing-markdown">
            <ReactMarkdown
                components={{
                    p: ({ node, children, ...props }) => {
                        const index = node?.position?.start?.line || 0;
                        const isChanged = changedSections.has(index);
                        return (
                            <p className={isChanged ? 'highlight-changed' : ''} {...props}>
                                {children}
                            </p>
                        );
                    },
                    h1: ({ node, children, ...props }) => {
                        const index = node?.position?.start?.line || 0;
                        const isChanged = changedSections.has(index);
                        return (
                            <h1 className={isChanged ? 'highlight-changed' : ''} {...props}>
                                {children}
                            </h1>
                        );
                    },
                    h2: ({ node, children, ...props }) => {
                        const index = node?.position?.start?.line || 0;
                        const isChanged = changedSections.has(index);
                        return (
                            <h2 className={isChanged ? 'highlight-changed' : ''} {...props}>
                                {children}
                            </h2>
                        );
                    },
                    h3: ({ node, children, ...props }) => {
                        const index = node?.position?.start?.line || 0;
                        const isChanged = changedSections.has(index);
                        return (
                            <h3 className={isChanged ? 'highlight-changed' : ''} {...props}>
                                {children}
                            </h3>
                        );
                    },
                    h4: ({ node, children, ...props }) => {
                        const index = node?.position?.start?.line || 0;
                        const isChanged = changedSections.has(index);
                        return (
                            <h4 className={isChanged ? 'highlight-changed' : ''} {...props}>
                                {children}
                            </h4>
                        );
                    },
                    ul: ({ node, children, ...props }) => {
                        const index = node?.position?.start?.line || 0;
                        const isChanged = changedSections.has(index);
                        return (
                            <ul className={isChanged ? 'highlight-changed' : ''} {...props}>
                                {children}
                            </ul>
                        );
                    },
                    ol: ({ node, children, ...props }) => {
                        const index = node?.position?.start?.line || 0;
                        const isChanged = changedSections.has(index);
                        return (
                            <ol className={isChanged ? 'highlight-changed' : ''} {...props}>
                                {children}
                            </ol>
                        );
                    }
                }}
            >
                {displayContent}
            </ReactMarkdown>
        </div>
    );
};

/**
 * Split markdown content into sections for comparison
 */
function splitIntoSections(content) {
    if (!content) return [];
    // Split by double newlines or headers to get logical sections
    return content.split(/\n(?=#{1,6}\s|\n)/g).filter(s => s.trim());
}

export default TypingText;
